import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CloudUpload, Loader2, Trash2, FileText, Settings2, ArrowRightCircle, Image, ShieldCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  flushImageUploadOutbox,
  listImageUploadOutboxItems,
  removeImageUploadOutboxItem,
  subscribeImageUploadOutboxPendingCount,
  type ImageUploadOutboxListItem,
} from "@/lib/image-upload-outbox";
import {
  flushCrmSyncOutbox,
  listCrmSyncOps,
  removeCrmSyncOp,
  subscribeCrmSyncPendingCount,
  type CrmSyncOpListItem,
} from "@/lib/crm-sync-outbox";
import {
  getOfflineSyncRequireReview,
  setOfflineSyncRequireReview,
} from "@/lib/offline-sync-settings";
import { notify } from "@/lib/notify";
import {
  clearOfflineSyncFailureLog,
  listOfflineSyncFailures,
  markAllOfflineSyncFailuresRead,
  removeOfflineSyncFailure,
  subscribeOfflineSyncFailureUnreadCount,
  subscribeOfflineSyncFailureLogCount,
  type OfflineSyncFailureRecord,
} from "@/lib/offline-sync-failure-log";

const OP_ICON: Record<string, typeof FileText> = {
  note: FileText,
  deal_patch: Settings2,
  deal_stage: ArrowRightCircle,
};

export function ImageUploadOutboxIndicator() {
  const { t } = useTranslation();
  const [imageCount, setImageCount] = useState(0);
  const [crmCount, setCrmCount] = useState(0);
  const totalCount = imageCount + crmCount;
  const [open, setOpen] = useState(false);
  const [imageItems, setImageItems] = useState<ImageUploadOutboxListItem[]>([]);
  const [crmItems, setCrmItems] = useState<CrmSyncOpListItem[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const [reviewMode, setReviewMode] = useState(() => getOfflineSyncRequireReview());
  const [failureItems, setFailureItems] = useState<OfflineSyncFailureRecord[]>([]);
  const [unreadFailureCount, setUnreadFailureCount] = useState(0);
  const [failureLogCount, setFailureLogCount] = useState(0);

  useEffect(() => subscribeImageUploadOutboxPendingCount(setImageCount), []);
  useEffect(() => subscribeCrmSyncPendingCount(setCrmCount), []);
  useEffect(() => subscribeOfflineSyncFailureUnreadCount(setUnreadFailureCount), []);
  useEffect(() => subscribeOfflineSyncFailureLogCount(setFailureLogCount), []);

  const refreshItems = useCallback(async () => {
    setLoadingList(true);
    try {
      const [imgs, crm, failures] = await Promise.all([
        listImageUploadOutboxItems(),
        listCrmSyncOps(),
        listOfflineSyncFailures(),
      ]);
      setImageItems(imgs);
      setCrmItems(crm);
      setFailureItems(failures);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      void refreshItems();
      void markAllOfflineSyncFailuresRead();
    }
  }, [open, refreshItems]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      void markAllOfflineSyncFailuresRead();
    }
  };

  const handleFlush = async () => {
    setFlushing(true);
    const before = totalCount;
    try {
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        notify.error(t("uploadOutbox.flushOffline"));
        return;
      }
      await Promise.all([flushImageUploadOutbox(), flushCrmSyncOutbox()]);
      const [imgs, crm] = await Promise.all([listImageUploadOutboxItems(), listCrmSyncOps()]);
      setImageItems(imgs);
      setCrmItems(crm);
      const remaining = imgs.length + crm.length;
      const sent = Math.max(0, before - remaining);
      if (remaining === 0) {
        notify.success(t("uploadOutbox.flushAllSent"));
      } else if (sent > 0) {
        notify.info(t("uploadOutbox.flushPartial", { sent, remaining }));
      } else {
        // Nothing went through — items are kept and will be retried automatically.
        notify.info(t("uploadOutbox.flushStillPending", { count: remaining }));
      }
    } finally {
      setFlushing(false);
    }
  };

  const handleRemoveImage = async (id: string) => {
    await removeImageUploadOutboxItem(id);
    await refreshItems();
  };

  const handleRemoveCrm = async (id: string) => {
    await removeCrmSyncOp(id);
    await refreshItems();
  };

  const handleRemoveFailure = async (id: string) => {
    await removeOfflineSyncFailure(id);
    await refreshItems();
  };

  const handleClearFailures = async () => {
    await clearOfflineSyncFailureLog();
    await refreshItems();
  };

  const showIndicator = totalCount > 0 || failureLogCount > 0;

  if (!showIndicator && !open) {
    return null;
  }

  const badgeCount =
    totalCount > 0 ? totalCount : unreadFailureCount > 0 ? unreadFailureCount : failureLogCount;

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="relative h-9 w-9 md:h-10 md:w-10"
        onClick={() => setOpen(true)}
        aria-label={t("uploadOutbox.title")}
      >
        <CloudUpload className="h-5 w-5" />
        {badgeCount > 0 && (
          <Badge
            variant={totalCount > 0 ? "destructive" : "secondary"}
            className="absolute -right-0.5 -top-0.5 h-5 min-w-5 px-1 text-[10px] leading-none flex items-center justify-center p-0"
          >
            {badgeCount > 99 ? "99+" : badgeCount}
          </Badge>
        )}
      </Button>

      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
          <SheetHeader>
            <SheetTitle>{t("uploadOutbox.title")}</SheetTitle>
            <SheetDescription>{t("uploadOutbox.description")}</SheetDescription>
          </SheetHeader>

          <div className="mt-4 flex flex-col gap-3 flex-1 min-h-0">
            <div className="flex items-center justify-between gap-2 rounded-md border border-border p-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="review-toggle" className="text-sm cursor-pointer">
                  {t("uploadOutbox.reviewMode")}
                </Label>
              </div>
              <Switch
                id="review-toggle"
                checked={reviewMode}
                onCheckedChange={(v) => {
                  setReviewMode(v);
                  setOfflineSyncRequireReview(v);
                }}
              />
            </div>

            <Button type="button" onClick={handleFlush} disabled={flushing || totalCount === 0} className="w-full">
              {flushing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {reviewMode ? t("uploadOutbox.approveAndSend") : t("uploadOutbox.flushNow")}
            </Button>

            <div className="flex-1 overflow-y-auto rounded-md border border-border min-h-[120px] flex flex-col">
              {loadingList ? (
                <div className="flex items-center justify-center p-8 text-muted-foreground text-sm">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  {t("common.loading")}
                </div>
              ) : imageItems.length === 0 && crmItems.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground text-center">{t("uploadOutbox.empty")}</p>
              ) : (
                <ul className="divide-y divide-border">
                  {imageItems.map((item) => (
                    <li key={item.id} className="p-3 flex flex-col gap-2">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Image className="h-3.5 w-3.5 shrink-0" />
                        {new Date(item.createdAt).toLocaleString()}
                      </div>
                      <div className="text-sm font-medium">{item.fileNames.join(", ")}</div>
                      <div className="text-xs text-muted-foreground">
                        {item.subfolderType}
                        {item.productSlug ? ` · ${item.productSlug}` : null}
                        {item.context.dealId ? ` · deal ${item.context.dealId.slice(0, 8)}…` : null}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="self-start"
                        onClick={() => void handleRemoveImage(item.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        {t("uploadOutbox.remove")}
                      </Button>
                    </li>
                  ))}
                  {crmItems.map((item) => {
                    const Icon = OP_ICON[item.type] ?? FileText;
                    return (
                      <li key={item.id} className="p-3 flex flex-col gap-2">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Icon className="h-3.5 w-3.5 shrink-0" />
                          {new Date(item.createdAt).toLocaleString()}
                        </div>
                        <div className="text-sm font-medium">{item.preview}</div>
                        <div className="text-xs text-muted-foreground">
                          deal {item.dealId.slice(0, 8)}…
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="self-start"
                          onClick={() => void handleRemoveCrm(item.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          {t("uploadOutbox.remove")}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {failureItems.length > 0 && (
                <div className="border-t border-border mt-auto">
                  <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/40">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                      {t("uploadOutbox.failureLogTitle")}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => void handleClearFailures()}
                    >
                      {t("uploadOutbox.failureLogClear")}
                    </Button>
                  </div>
                  <p className="px-3 pb-2 text-xs text-muted-foreground">{t("uploadOutbox.failureLogHint")}</p>
                  <ul className="divide-y divide-border">
                    {failureItems.map((item) => (
                      <li key={item.id} className="p-3 flex flex-col gap-1.5">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {item.kind === "image" ? (
                            <Image className="h-3.5 w-3.5 shrink-0" />
                          ) : (
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                          )}
                          {new Date(item.at).toLocaleString()}
                          {!item.read && (
                            <Badge variant="destructive" className="h-4 px-1 text-[10px]">
                              {t("uploadOutbox.failureLogNew")}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm font-medium break-words">{item.title}</div>
                        <div className="text-xs text-muted-foreground space-y-0.5">
                          {item.status > 0 && (
                            <div>{t("uploadOutbox.failureLogStatus", { status: item.status })}</div>
                          )}
                          {item.errorCode && (
                            <div>{t("uploadOutbox.failureLogErrorCode", { code: item.errorCode })}</div>
                          )}
                          {item.message && <div className="break-words">{item.message}</div>}
                          {item.dealId && <div>deal {item.dealId.slice(0, 8)}…</div>}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="self-start mt-1"
                          onClick={() => void handleRemoveFailure(item.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          {t("uploadOutbox.failureLogDismiss")}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
